import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useOfflineLibrary } from '@/hooks/useOfflineLibrary';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { articleSummary, articleTitle, getTagValue } from '@/lib/nip54';
import type { OfflineArticle } from '@/lib/offlineLibrary';

interface OfflineArticleCardProps {
  article: OfflineArticle;
}

/**
 * A saved-for-offline article card.
 *
 * Deliberately does not reuse `ArticleCard`: that card resolves the
 * author via `useAuthor` (a relay query) and shows a `StarButton`
 * (which both queries and publishes). Neither works without a
 * connection, which is exactly the situation this card exists for. So
 * the author is read from the snapshot captured at save time, and the
 * only action offered is removing the article from the library.
 */
export function OfflineArticleCard({ article }: OfflineArticleCardProps) {
  const { remove } = useOfflineLibrary();
  const { event, author, savedAt } = article;

  const slug = getTagValue(event, 'd') ?? '';
  const npub = nip19.npubEncode(event.pubkey);
  const displayName = author?.name ?? `${event.pubkey.slice(0, 8)}…`;
  const summary = articleSummary(event) ?? event.content.slice(0, 160);
  const picture = sanitizeUrl(author?.picture);

  return (
    <Card className="flex flex-col transition-shadow hover:shadow-md">
      <CardHeader>
        <CardTitle className="text-lg">
          <Link to={`/w/${encodeURIComponent(slug)}/${npub}`} className="hover:underline">
            {articleTitle(event)}
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-end space-y-3">
        <p className="line-clamp-3 text-sm text-muted-foreground break-words">{summary}</p>
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
            <Avatar className="size-5">
              <AvatarImage src={picture} alt={displayName} />
              <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="truncate">{displayName}</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={() => remove(event)}
            title="Remove from offline library"
            aria-label={`Remove "${articleTitle(event)}" from offline library`}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
        {savedAt > 0 && (
          <p className="text-xs text-muted-foreground/70">
            Saved{' '}
            <time dateTime={new Date(savedAt).toISOString()}>{new Date(savedAt).toLocaleDateString()}</time>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
